import { CACHE_NAMESPACE, CACHE_TTL, fetchThroughCache } from "@/entities/cache";
import {
  fetchCampaignList,
  fetchCreativeList,
  fetchOfferList,
  fetchTriggerList,
} from "@/entities/filter-options";
import { fetchTrafficProviders } from "@/entities/providers";
import { REPORT_STRING_OPTIONS } from "@/entities/report";
import type { ReportFilters } from "@/entities/report";
import type { ComparisonMethod, ComparisonTarget } from "./comparison.types";

// Human-readable labels for the method slugs (used in the comparison list).
export const COMPARISON_METHOD_LABELS: Record<ComparisonMethod, string> = {
  factor: "Факторный анализ",
  bayesian: "Байесовский анализ",
};

// The ReportFilters list-dimensions a comparison target can be scoped by.
export type TargetFilterField = Extract<
  keyof ReportFilters,
  "providers" | "campaigns" | "offers" | "triggers" | "creatives"
>;

// A comparison target is one of the report's filterable dimensions. Everything
// about it lives here — the report section key (from REPORT_STRING_OPTIONS), the
// ReportFilters field used to scope a request, and the list fetcher — so the
// mapping isn't duplicated across the compare pages.
export type TargetOption = {
  value: ComparisonTarget;
  // Russian noun used for the step chips, the title ("Выберите …") and the
  // Field labels.
  label: string;
  // The matching report "Разрез" section key (REPORT_STRING_OPTIONS).
  section: number;
  // The ReportFilters dimension a request is scoped by for this target.
  filterField: TargetFilterField;
  // Loads the selectable items for this target ({ id, name }).
  fetch: () => Promise<{ id: number; name: string }[]>;
  // Cache namespace for the (provider-unscoped) item list, so the compare pages
  // can resolve names → ids from on-device cache when offline.
  cacheNamespace: string;
};

export const TARGET_OPTIONS: TargetOption[] = [
  {
    value: "trafficProvider",
    label: "Поставщик",
    section: REPORT_STRING_OPTIONS.trafficProviders,
    filterField: "providers",
    fetch: fetchTrafficProviders,
    cacheNamespace: CACHE_NAMESPACE.TrafficProviders,
  },
  {
    value: "campaign",
    label: "Кампания",
    section: REPORT_STRING_OPTIONS.campaign,
    filterField: "campaigns",
    fetch: fetchCampaignList,
    cacheNamespace: CACHE_NAMESPACE.CampaignList,
  },
  {
    value: "offer",
    label: "Оффер",
    section: REPORT_STRING_OPTIONS.offers,
    filterField: "offers",
    fetch: fetchOfferList,
    cacheNamespace: CACHE_NAMESPACE.OfferList,
  },
  {
    value: "trigger",
    label: "Триггер",
    section: REPORT_STRING_OPTIONS.triggers,
    filterField: "triggers",
    fetch: fetchTriggerList,
    cacheNamespace: CACHE_NAMESPACE.TriggerList,
  },
  {
    value: "creative",
    label: "Креатив",
    section: REPORT_STRING_OPTIONS.creatives,
    filterField: "creatives",
    fetch: fetchCreativeList,
    cacheNamespace: CACHE_NAMESPACE.CreativeList,
  },
];

export const TARGET_BY_VALUE: Record<ComparisonTarget, TargetOption> = Object.fromEntries(
  TARGET_OPTIONS.map((option) => [option.value, option]),
) as Record<ComparisonTarget, TargetOption>;

// Resolve a target's selectable items ({ id, name }), keeping the result fresh
// when online and falling back to the on-device cache offline. The compare
// detail pages use this to map saved target names → ids without a network.
export function loadTargetItems(target: TargetOption): Promise<{ id: number; name: string }[]> {
  return fetchThroughCache(target.cacheNamespace, "all", () => target.fetch(), {
    ttl: CACHE_TTL.Long,
  });
}
